import ActivityLog from '@components/activityLog/ActivityLog'
import { subscriptionAwaitingTransferStatuses } from '@models/membership/Subscription'
import {
  acceptSubscription,
  confirmSubscriptionTransfer,
  rejectSubscription,
} from '@services/membership/SubscriptionService'
import { useAppSelector } from '@store/hooks'
import { selectSelectedSubscription } from '@store/membership/SubscriptionReducer'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tabs,
  TabsProps,
  Typography,
} from 'antd'
import { useState } from 'react'
import MembershipProfile from '../membership-profile/MembershipProfile'
import { MembershipSubscriptionAvatar } from './Avatar'
import { SubscriptionFormAnswers } from './FormAnswers'
import { SubscriptionPaymentData } from './PaymentData'

const { Text, Title } = Typography

interface Props {
  isVisible: boolean
  onClose: () => void
}
const MembershipSubscriptionReviewModal = ({
  onClose,
  isVisible = false,
}: Props) => {
  const subscription = useAppSelector(selectSelectedSubscription())

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [reason, setReason] = useState<string | undefined>(undefined)
  const [sendEmail, setSendEmail] = useState<boolean>(true)

  const handleAccept = () => {
    if (!subscription) return
    setIsLoading(true)
    acceptSubscription(subscription.id)
      .then(() => {
        setError(undefined)
        setIsLoading(false)
        handleClose()
      })
      .catch((error) => {
        setError(error.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }
  const handleReject = () => {
    if (!subscription) return
    setIsLoading(true)
    rejectSubscription(subscription.id, reason, sendEmail)
      .then(() => {
        setReason(undefined)
        setError(undefined)
        setIsLoading(false)
        handleClose()
      })
      .catch((error) => {
        setError(error.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }
  const handleConfirmTransfer = () => {
    if (!subscription) return
    setIsLoading(true)
    confirmSubscriptionTransfer(subscription.id)
      .then(() => {
        setError(undefined)
        setIsLoading(false)
        handleClose()
      })
      .catch((error) => {
        setError(error.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }
  const handleClose = () => {
    setReason(undefined)
    setError(undefined)
    setIsLoading(false)
    onClose()
  }

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Form answers',
      children: subscription && (
        <Flex vertical gap={20}>
          <Title level={5} className="m-0">
            Plan
          </Title>
          <Alert
            title={
              <MembershipSubscriptionAvatar
                plan
                price
                coupon
                createdAt
                id={subscription.id}
              />
            }
          />
          {(subscription.membershipProfiles ?? []).map((profile) => (
            <MembershipProfile.Description key={profile} id={profile} />
          ))}
          <SubscriptionFormAnswers id={subscription.id} />
        </Flex>
      ),
    },
    {
      key: '2',
      label: 'Payment',
      children: subscription && (
        <SubscriptionPaymentData id={subscription.id} />
      ),
    },
    {
      key: '3',
      label: 'Activities',
      children: subscription && subscription.id && (
        <Flex vertical gap={20}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <ActivityLog.Timeline
                title={"Subscription's activities"}
                entity="membership-subscription"
                entityId={subscription.id}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            {(subscription.membershipProfiles ?? []).map((id) => {
              const spanSize = 24 / subscription.membershipProfiles!.length
              return (
                <Col span={spanSize} key={id}>
                  <ActivityLog.Timeline
                    title={"Profile's activities"}
                    entity="membership-profile"
                    entityId={id}
                  />
                </Col>
              )
            })}
          </Row>
        </Flex>
      ),
    },
  ]

  return (
    <Modal
      width={'60%'}
      closeIcon={false}
      open={isVisible}
      onCancel={handleClose}
      footer={
        subscription && (
          <Space
            orientation="vertical"
            className="w-full"
            style={{
              borderTop: '1px solid #f0f0f0',
              paddingTop: 10,
              marginTop: 20,
            }}
          >
            {(() => {
              const isAwaitingTransfer = subscriptionAwaitingTransferStatuses.includes(
                subscription.status
              )
              const isReviewable =
                subscription.status === 'to_review' || isAwaitingTransfer

              if (isReviewable) return null

              return (
                <Alert
                  type="warning"
                  title={
                    <Space className="w-full">
                      <Text>
                        Request will be reviewable once payment infos will be
                        provided
                      </Text>
                    </Space>
                  }
                />
              )
            })()}
            <Space>
              <Button onClick={handleClose}>Close</Button>

              <Popconfirm
                placement="topRight"
                title={'Are you sure to reject this subscription?'}
                description={
                  <Space vertical>
                    <Text>
                      {subscriptionAwaitingTransferStatuses.includes(
                        subscription.status
                      )
                        ? 'This action cannot be undone. The subscription will be deleted.'
                        : 'This action cannot be undone and the card pre-auth will be cancelled'}
                    </Text>
                    <Input
                      disabled={!sendEmail}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Add a reason for the client (optional)"
                    />
                    <Space>
                      <Checkbox
                        checked={sendEmail}
                        onChange={(e) => {
                          setSendEmail(e.target.checked)
                        }}
                      />
                      <Text>
                        Send an email to the member to inform about the
                        rejection
                      </Text>
                    </Space>
                  </Space>
                }
                okText="Reject"
                okButtonProps={{
                  danger: true,
                  size: 'middle',
                }}
                onConfirm={handleReject}
                cancelButtonProps={{ style: { display: 'none' } }}
              >
                <Button
                  danger
                  disabled={
                    subscription.status !== 'to_review' &&
                    !subscriptionAwaitingTransferStatuses.includes(
                      subscription.status
                    )
                  }
                >
                  Reject
                </Button>
              </Popconfirm>
              {subscriptionAwaitingTransferStatuses.includes(
                subscription.status
              ) ? (
                <Popconfirm
                  placement="topRight"
                  title={'Confirm bank transfer received?'}
                  description={
                    <Text>
                      Mark this subscription as paid by bank transfer. The
                      member will be activated{' '}
                      {subscription.status === 'awaiting_transfer_renewal'
                        ? 'and the renewal date will be extended by one year.'
                        : 'immediately.'}
                    </Text>
                  }
                  okText="Confirm transfer"
                  okButtonProps={{ size: 'middle' }}
                  onConfirm={handleConfirmTransfer}
                  cancelButtonProps={{ style: { display: 'none' } }}
                >
                  <Button type="primary">Confirm transfer</Button>
                </Popconfirm>
              ) : (
                <Button
                  type="primary"
                  onClick={handleAccept}
                  disabled={subscription.status !== 'to_review'}
                >
                  Accept
                </Button>
              )}
            </Space>
          </Space>
        )
      }
    >
      {!subscription || isLoading ? (
        <Spin />
      ) : (
        <>
          {error && <Alert type="error" title="This is an error message" />}
          <Tabs defaultActiveKey="1" items={items} />
        </>
      )}
    </Modal>
  )
}
type MembershipSubscriptionReviewModalType = {
  ReviewModal: typeof MembershipSubscriptionReviewModal
}

export {
  MembershipSubscriptionReviewModal,
  type MembershipSubscriptionReviewModalType,
}
